/**
 * Dashboard cost/revenue chart derivations for CHO-752.
 *
 * React-free so the same code runs in the client bundle and under the Node
 * test runner (server/__tests__/dashboard-costs.test.ts), mirroring the
 * service-groups.ts precedent.
 *
 * Currency policy (operator decision 2026-09-18): no FX conversion, no silent
 * mixing — every series stays in its native currency and panes are split
 * per currency.
 */

// ---------------------------------------------------------------------------
// Input shapes (mirror the API payloads)
// ---------------------------------------------------------------------------

export interface RevenueMonthRow {
  month: string; // 'YYYY-MM', bucketed on checkin_date by /api/bookings/summary
  property_id: number;
  property_name: string;
  currency: string;
  total_payout: number;
}

export interface MonthlyCostRow {
  month: string; // 'YYYY-MM', bucketed on costs.date by /api/costs/monthly
  property_id: number;
  property_name: string;
  currency: string;
  category: string; // raw slash-separated e.g. 'utilities/dewa'
  total: number;
}

export interface ExcludedCount {
  property_id: number;
  count: number;
}

export interface CostsMonthlyResponse {
  by_month: MonthlyCostRow[];
  excluded: ExcludedCount[];
}

/** currency -> month -> property name -> amount */
export type MonthlyAmountLookup = Map<string, Map<string, Map<string, number>>>;

export const MORTGAGE_PRINCIPAL_CATEGORY = 'mortgage/principal';

// ---------------------------------------------------------------------------
// Time filtering (shared with the Dashboard TIME_FILTERS control)
// ---------------------------------------------------------------------------

/**
 * Cutoffs for the current and previous period of a TIME_FILTERS entry.
 * Months is null ("All") -> no cutoff and no previous period.
 * Cutoffs are 'YYYY-MM' strings; month strings compare lexicographically.
 */
export function filterPeriod(
  months: number | null,
  now?: Date,
): { cutoff: string | null; prevCutoff: string | null } {
  if (months == null) return { cutoff: null, prevCutoff: null };
  const ref = now ?? new Date();
  return { cutoff: shiftMonths(ref, months), prevCutoff: shiftMonths(ref, months * 2) };
}

function shiftMonths(d: Date, delta: number): string {
  const x = new Date(d.getTime());
  x.setMonth(x.getMonth() - delta);
  return x.toISOString().slice(0, 7);
}

/** Sorted unique months, optionally restricted to month >= cutoff. */
export function monthsInRange(months: Iterable<string>, cutoff: string | null): string[] {
  const all = Array.from(new Set(months)).sort();
  return cutoff ? all.filter((m) => m >= cutoff) : all;
}

// ---------------------------------------------------------------------------
// Revenue / cost lookups — the single shared derivations behind every chart
// ---------------------------------------------------------------------------

/**
 * THE revenue derivation consumed by both the revenue chart and the profit
 * chart. Feed it summary.by_month from /api/bookings/summary; never build a
 * second revenue mapping elsewhere.
 */
export function buildRevenueLookup(byMonth: RevenueMonthRow[]): MonthlyAmountLookup {
  const lookup: MonthlyAmountLookup = new Map();
  for (const r of byMonth) {
    addAmount(lookup, r.currency, r.month, r.property_name, r.total_payout);
  }
  return lookup;
}

/**
 * Cost totals per currency/month/property. By default sums every category;
 * pass includePrincipal: false to drop mortgage/principal (profit toggle).
 */
export function buildCostLookup(
  byMonth: MonthlyCostRow[],
  opts: { includePrincipal?: boolean } = {},
): MonthlyAmountLookup {
  const includePrincipal = opts.includePrincipal ?? true;
  const lookup: MonthlyAmountLookup = new Map();
  for (const r of byMonth) {
    if (!includePrincipal && r.category === MORTGAGE_PRINCIPAL_CATEGORY) continue;
    addAmount(lookup, r.currency, r.month, r.property_name, r.total);
  }
  return lookup;
}

function addAmount(
  lookup: MonthlyAmountLookup,
  currency: string,
  month: string,
  property: string,
  amount: number,
): void {
  let byMonth = lookup.get(currency);
  if (!byMonth) {
    byMonth = new Map();
    lookup.set(currency, byMonth);
  }
  let byProperty = byMonth.get(month);
  if (!byProperty) {
    byProperty = new Map();
    byMonth.set(month, byProperty);
  }
  byProperty.set(property, (byProperty.get(property) ?? 0) + amount);
}

/** Properties (line names) for one currency pane, in first-seen order. */
export function propertiesForCurrency(
  revenue: MonthlyAmountLookup,
  costs: MonthlyAmountLookup,
  currency: string,
): string[] {
  const names: string[] = [];
  const seen = new Set<string>();
  for (const source of [revenue.get(currency), costs.get(currency)]) {
    if (!source) continue;
    for (const byProperty of source.values()) {
      for (const name of byProperty.keys()) {
        if (!seen.has(name)) {
          seen.add(name);
          names.push(name);
        }
      }
    }
  }
  return names;
}

// ---------------------------------------------------------------------------
// Chart row builders
// ---------------------------------------------------------------------------

export interface CombinedChartRow {
  month: string;
  [key: string]: string | number | null;
}

/**
 * One currency pane of the Revenue Over Time chart: per property a solid
 * revenue line (key = property name, absent when no revenue that month —
 * recharts connectNulls bridges) plus a dashed cost line (key =
 * "<property> costs", 0-filled so cost gaps mean zero spend).
 */
export function combinedChartRows(
  revenue: MonthlyAmountLookup,
  costs: MonthlyAmountLookup,
  currency: string,
  months: string[],
): CombinedChartRow[] {
  return months.map((month) => {
    const row: CombinedChartRow = { month };
    for (const [property, amount] of revenue.get(currency)?.get(month) ?? []) {
      row[property] = Math.round(amount * 100) / 100;
    }
    for (const property of propertiesForCurrency(revenue, costs, currency)) {
      const cost = costs.get(currency)?.get(month)?.get(property);
      row[`${property} costs`] = cost == null ? 0 : Math.round(cost * 100) / 100;
    }
    return row;
  });
}

/**
 * Profit rows: profit[month][property] = revenue - costs, missing sides
 * treated as 0 (a costs-only month renders as negative profit, not a gap).
 * Consumes the SAME revenue lookup as combinedChartRows — the identity the
 * dashboard-costs tests pin.
 */
export function profitChartRows(
  revenue: MonthlyAmountLookup,
  costs: MonthlyAmountLookup,
  currency: string,
  months: string[],
): CombinedChartRow[] {
  return months.map((month) => {
    const row: CombinedChartRow = { month };
    for (const property of propertiesForCurrency(revenue, costs, currency)) {
      const rev = revenue.get(currency)?.get(month)?.get(property) ?? 0;
      const cost = costs.get(currency)?.get(month)?.get(property) ?? 0;
      row[property] = Math.round((rev - cost) * 100) / 100;
    }
    return row;
  });
}

// ---------------------------------------------------------------------------
// Per-unit cost breakdown (prefix rollup + raw-category detail)
// ---------------------------------------------------------------------------

/** Rollup key: everything before the first '/'; no slash -> whole category. */
export function rollupPrefix(category: string): string {
  const idx = category.indexOf('/');
  return idx === -1 ? category : category.slice(0, idx);
}

export interface BreakdownDetailRow {
  category: string; // raw category, never flattened
  total: number;
  share: number; // 0..1 of the unit's period total
  trend: number | null; // percent change vs previous period
}

export interface BreakdownRow {
  prefix: string;
  total: number;
  share: number;
  trend: number | null;
  detail: BreakdownDetailRow[];
}

export interface UnitBreakdown {
  rows: BreakdownRow[];
  total: number;
  prevTotal: number | null;
}

interface CategoryBucket {
  total: number;
  prevTotal: number;
}

function bucketsFor(rows: MonthlyCostRow[], prevRows: MonthlyCostRow[]): Map<string, CategoryBucket> {
  const prevByCategory = new Map<string, number>();
  for (const r of prevRows) {
    prevByCategory.set(r.category, (prevByCategory.get(r.category) ?? 0) + r.total);
  }
  const map = new Map<string, CategoryBucket>();
  for (const r of rows) {
    const bucket = map.get(r.category) ?? {
      total: 0,
      prevTotal: prevByCategory.get(r.category) ?? 0,
    };
    bucket.total += r.total;
    map.set(r.category, bucket);
  }
  // Categories that dropped to zero this period still get a trend baseline.
  for (const [category, prevTotal] of prevByCategory) {
    if (!map.has(category)) map.set(category, { total: 0, prevTotal });
  }
  return map;
}

function trendPct(current: number, previous: number): number | null {
  if (previous <= 0) return null;
  return Math.round(((current - previous) / previous) * 1000) / 10;
}

function share(part: number, whole: number): number {
  return whole > 0 ? Math.round((part / whole) * 1000) / 1000 : 0;
}

/**
 * Rollup of one property's costs for the selected period, with per-prefix
 * trend vs prevRows (the equal-length previous period). Pass prevRows = []
 * for the "All" filter — every trend then renders as "—".
 */
export function buildUnitBreakdown(
  rows: MonthlyCostRow[],
  prevRows: MonthlyCostRow[],
): UnitBreakdown {
  const current = bucketsFor(rows, prevRows);

  const total = rows.reduce((sum, r) => sum + r.total, 0);
  const prevTotal = prevRows.length
    ? prevRows.reduce((sum, r) => sum + r.total, 0)
    : null;

  const byPrefix = new Map<string, BreakdownRow & { prevTotal: number }>();
  for (const [category, bucket] of current) {
    const prefix = rollupPrefix(category);
    let row = byPrefix.get(prefix);
    if (!row) {
      row = { prefix, total: 0, share: 0, trend: null, detail: [], prevTotal: 0 };
      byPrefix.set(prefix, row);
    }
    row.total += bucket.total;
    row.prevTotal += bucket.prevTotal;
    row.detail.push({
      category,
      total: bucket.total,
      share: share(bucket.total, total),
      trend: trendPct(bucket.total, bucket.prevTotal),
    });
  }

  for (const row of byPrefix.values()) {
    row.share = share(row.total, total);
    row.trend = trendPct(row.total, row.prevTotal);
    row.detail.sort((a, b) => b.total - a.total);
  }

  return {
    rows: Array.from(byPrefix.values()).sort((a, b) => b.total - a.total),
    total,
    prevTotal,
  };
}

/** Excluded-row count for one property from /api/costs/monthly's excluded array. */
export function excludedCountFor(excluded: ExcludedCount[], propertyId: number): number {
  return excluded.find((e) => e.property_id === propertyId)?.count ?? 0;
}
